import * as React from 'react'

import TranslationError from './TranslationError'

export interface Translation {
  [key: string]: string | Translation
}
export interface Interpolations {
  [key: string]: React.ReactNode
}

const getValueAt = (object: object, path: string, fallback?: string): string | object => {
  const fields = path.split('.')
  const currentPath: string[] = []
  const translation: string | object = fields.reduce((a, c) => {
    if (a) {
      currentPath.push(c)
      return a[c]
    }

    return a
  }, object)

  if (translation === undefined) {
    if (fallback) {
      return getValueAt(object, fallback)
    }

    const missingPath = currentPath.join('.')
    throw new TranslationError(
      `Translation missing for '${missingPath}'`,
      missingPath
    )
  }

  return translation
}

export const getPartial = (language: Translation) => (
  ...paths: string[]
): string | object => {
  let value: null | string | object = null
  let error = null

  paths.forEach((path, i) => {
    try {
      if (value) {
        return
      }
      value = getValueAt(language, path, paths[i + 1])
    } catch (e) {
      error = e
    }
  })

  if (!value) {
    throw error
  }

  return value
}

export const translate = (language: Translation) => {
  if (!language) throw new TranslationError('Language not found')

  return (token: string, fallback?: string): string => {
    const translation = getValueAt(language, token, fallback)

    if (typeof translation !== 'string') {
      throw new TranslationError('Token not resolved to a string.')
    }

    return translation
  }
}

export const translateWithInterpolations = (language: Translation) => {
  if (!language) throw new TranslationError()

  return (
    token: string,
    interpolations?: Interpolations
  ): React.ReactNodeArray => {
    const parts = translate(language)(token).split(/({{[^{}]*}})/)
    const translation = parts.map((part: string, index: number) => {
      const match = part.match(/{{([^{}]*)}}/)
      return match && interpolations ? (
        <React.Fragment key={`${part}-${index}`}>
          {interpolations[match[1]]}
        </React.Fragment>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      )
    })

    return translation
  }
}

export default (language: Translation) => ({
  translate: translate(language),
  translateWithInterpolations: translateWithInterpolations(language),
  getPartial: getPartial(language)
})
